import axios from "axios";
import { URL_PASSWORD_CREATE, URL_USERS } from "constants/urlApis";
import { endpointError } from "utils/codeErrorsTimeout";
import { isNullOrUndefined } from "utils/validations";
import useLogin from "hooks/useLogin";

export default function useUser() {
  const { jwt } = useLogin();
  const headers = {
    Authorization: `Bearer ${jwt}`,
  };

  /**
   * Fetches a list of users based on the specified filters.
   * @param {number} page - The page number of the users list to fetch.
   * @param {number} per_page - The number of users per page to fetch.
   * @param {string} search - (Not currently used) A search term to filter users.
   * @param {string|null|undefined} status - The status of users to filter (e.g. active, inactive).
   * @returns {Promise<Object>} A promise that resolves to an object containing user data.
   * @throws {Error} Throws an error if the request fails.
   */
  const getUsers = async (page, per_page, search, status) => {
    if (
      isNullOrUndefined(status) === false &&
      isNullOrUndefined(page) === false &&
      isNullOrUndefined(per_page) === false
    ) {
      const responseUsersList = await axios.get(
        `${URL_USERS}/users/?status=${status}&page=${page}&per_page=${per_page}`,
        {
          headers,
          endpointError: endpointError.user.getUsers,
        }
      );
      return responseUsersList.data;
    }

    if (
      isNullOrUndefined(search) === false &&
      isNullOrUndefined(page) === false &&
      isNullOrUndefined(per_page) === false
    ) {
      const responseUsersList = await axios.get(
        `${URL_USERS}/users/?user_name=${search}&first_name=${search}&last_name=${search}&page=${page}&per_page=${per_page}`,
        {
          headers,
          endpointError: endpointError.user.getUsers,
        }
      );
      return responseUsersList.data;
    }

    const responseUsersList = await axios.get(
      `${URL_USERS}/users/?page=${page}&per_page=${per_page}`,
      {
        headers,
        endpointError: endpointError.user.getUsers,
      }
    );
    return responseUsersList.data;
  };

  /**
   * Fetches a list of Api users based on the specified filters.
   * @param {number} page - The page number of the users list to fetch.
   * @param {number} per_page - The number of users per page to fetch.
   * @param {string} search - (Not currently used) A search term to filter users.
   * @param {string|null|undefined} status - The status of users to filter (e.g. active, inactive).
   * @returns {Promise<Object>} A promise that resolves to an object containing user data.
   * @throws {Error} Throws an error if the request fails.
   */
  const getApiUsers = async (page, per_page, search, status) => {
    if (
      isNullOrUndefined(status) === false &&
      isNullOrUndefined(page) === false &&
      isNullOrUndefined(per_page) === false
    ) {
      const responseApiUsersList = await axios.get(
        `${URL_USERS}/admin-api/users/?status=${status}&page=${page}&per_page=${per_page}`,
        {
          headers,
          endpointError: endpointError.user.getApiUsers,
        }
      );
      return responseApiUsersList.data;
    }

    if (
      isNullOrUndefined(search) === false &&
      isNullOrUndefined(page) === false &&
      isNullOrUndefined(per_page) === false
    ) {
      const responseApiUsersList = await axios.get(
        `${URL_USERS}/admin-api/users/?api_name=${search}&api_username=${search}&page=${page}&per_page=${per_page}`,
        {
          headers,
          endpointError: endpointError.user.getApiUsers,
        }
      );
      return responseApiUsersList.data;
    }

    const responseApiUsersList = await axios.get(
      `${URL_USERS}/admin-api/users/?page=${page}&per_page=${per_page}`,
      {
        headers,
        endpointError: endpointError.user.getApiUsers,
      }
    );
    return responseApiUsersList.data;
  };

  /**
   * Asynchronously retrieves user data by username.
   * @param {string} username - The username of the user to retrieve.
   * @returns {Promise<Object>} A promise that resolves to the user data object.
   * @throws {Error} Throws an error if the request to the API fails.
   */
  const getUsersById = async (username) => {
    const response = await axios.get(`${URL_USERS}/users/?user_name=${username}`, {
      headers,
      endpointError: endpointError.user.getUsersById,
    });
    return response.data;
  };

  /**
   * Asynchronously retrieves user data by username.
   * @param {string} username - The username of the user to retrieve.
   * @returns {Promise<Object>} A promise that resolves to the user data object.
   * @throws {Error} Throws an error if the request to the API fails.
   */
  const getUsersByUUID = async (userUuid) => {
    const response = await axios.get(`${URL_USERS}/users/${userUuid}`, {
      headers,
      endpointError: endpointError.user.getUsersByUUID,
    });
    return response.data;
  };

  /**
   * Creates a new user by sending a POST request to the user API endpoint.
   * @param {Object} user - The user object containing user details.
   * @param {string} domainUser - The domain to append to the username.
   * @returns {Promise<Object>} The response object from the API call.
   * @throws {Error} Throws an error if the request fails.
   */
  const createUser = async (user, domainUser) => {
    user.user_name = `${user.user_name}@${domainUser}`;
    const response = await axios.post(`${URL_USERS}/users/`, user, {
      headers,
      endpointError: endpointError.user.createUser,
    });
    return response;
  };

  /**
   * Creates a new API user by sending a POST request to the user API endpoint.
   * @param {Object} user - The user object containing user details.
   * @param {string} domainUser - The domain to append to the username.
   * @returns {Promise<Object>} The response object from the API call.
   * @throws {Error} Throws an error if the request fails.
   */
  const createApiUser = async (user, domainUser) => {
    user.api_username = `${user.api_username}@${domainUser}`;
    const response = await axios.post(`${URL_USERS}/admin-api/users/`, user, {
      headers,
      endpointError: endpointError.user.createApiUser,
    });
    return response;
  };

  /**
   * Updates a user's information in the system.
   * @param {Object} user - The user object containing information to update.
   * @param {string} id - The unique identifier of the user to be updated.
   * @param {string} domainUser - The domain to be appended to the user name if it is modified.
   * @param {boolean} isModifiedUserName - Flag indicating whether the user name has been modified.
   * @returns {Promise<Object>} A promise that resolves to the response from the server.
   * @throws {Error} Throws an error if the request fails.
   */
  const updateUser = async (user, id, domainUser, isModifiedUserName) => {
    if (isModifiedUserName === true) {
      user.user_name = `${user.user_name}@${domainUser}`;
      delete user.uuid;
    } else {
      delete user.user_name;
    }
    const response = await axios.patch(`${URL_USERS}/users/${id}/`, user, {
      headers,
      endpointError: endpointError.user.updateUser,
    });
    return response;
  };

  /**
   * Sends a request to create a new password for a user.
   * @param {string} new_password - The new password to be set for the user.
   * @param {Object} paramsUser - An object containing user parameters.
   * @param {string} paramsUser.userId - The ID of the user whose password is being changed.
   * @param {string} paramsUser.token - The authorization token for the user.
   * @returns {Promise<Object>} - A promise that resolves to the response object from the server.
   * @throws {Error} - Throws an error if the request fails.
   */
  const createPassword = async (new_password, paramsUser) => {
    const { userId, token } = paramsUser;
    const response = await axios.patch(
      `${URL_PASSWORD_CREATE}/users/${userId}/new-password/`,
      new_password,
      {
        headers: { Authorization: `Bearer ${token}` },
        endpointError: endpointError.user.createPassword,
      }
    );
    return response;
  };

  /**
   * Updates the status of a user by sending a PATCH request to the specified API endpoint.
   * @param {string} user_id - The unique identifier of the user whose status is to be updated.
   * @param {Object} data - The data to update the user's status with. The structure of this object should match the API's expected format.
   * @returns {Promise<Object>} A promise that resolves to the response object from the API, which contains the details of the updated user status.
   * @throws {Error} Throws an error if the API request fails.
   */
  const updateStatus = async (user_id, data) => {
    const response = await axios.patch(`${URL_USERS}/users/${user_id}/status/`, data, {
      headers,
      endpointError: endpointError.user.updateStatus,
    });
    return response;
  };

  /**
   * Sends a password reset email to the specified user.
   * This function makes an API call to send a password reset email
   * to the given email address. It uses Axios for the HTTP request
   * and handles potential errors through endpointError configuration.
   * @param {string} email - The email address of the user who requested a password reset.
   * @returns {Promise<Object>} A promise that resolves to the response object from the API call.
   * @throws {Error} Throws an error if the request fails or if the email is invalid.
   */
  const emailPasswordReset = async (email) => {
    const response = await axios.post(`${URL_USERS}/users/send-email/password-reset/`, email, {
      endpointError: endpointError.user.emailPasswordReset,
    });
    return response;
  };

  /**
   * Sends a password reset request for the specified user.
   * @param {Object} data - The data to be sent for the password reset.
   * @param {Object} paramsUser - The user parameters necessary for the password reset request.
   * @param {string} paramsUser.userId - The ID of the user requesting the password reset.
   * @param {string} paramsUser.token - The authorization token for the user.
   * @returns {Promise<Object>} - A promise that resolves to the response from the password reset request.
   * @throws {Error} - Throws an error if the request fails.
   */
  const forgottenPasswordReset = async (data, paramsUser) => {
    const { userId, token } = paramsUser;
    const response = await axios.post(`${URL_USERS}/users/${userId}/password-reset/`, data, {
      headers: { Authorization: `Bearer ${token}` },
      endpointError: endpointError.user.forgottenPasswordReset,
    });
    return response;
  };

  /**
   * Fetches user logs by user ID with pagination.
   * @param {string|number} user_id - The ID of the user whose logs are to be retrieved.
   * @param {number} page - The page number to retrieve for pagination.
   * @param {number} per_page - The number of logs per page.
   * @returns {Promise<Object>} A promise that resolves to the user logs data in JSON format.
   * @throws {Error} Throws an error if the request fails or if the response contains an error.
   */
  const getUserLogsById = async (user_id, page, per_page) => {
    const response = await axios.get(
      `${URL_USERS}/users/${user_id}/logs/?page=${page}&per_page=${per_page}`,
      {
        headers,
        endpointError: endpointError.user.getUserLogsById,
      }
    );
    return response.data;
  };

  /**
   * Changes the password for a specific user.
   * @param {string} user_id - The ID of the user whose password is being changed.
   * @param {Object} data - The data containing the new password.
   * @param {string} data.newPassword - The new password for the user.
   * @param {string} [data.confirmPassword] - An optional confirmation password, if required.
   * @returns {Promise<Object>} The response object from the API containing the result of the password change.
   * @throws {Error} Throws an error if the request fails or the password change is unsuccessful.
   */
  const changePasswordByUser = async (user_id, data) => {
    const response = await axios.patch(`${URL_USERS}/users/${user_id}/password-change/`, data, {
      headers,
      endpointError: endpointError.user.changePasswordByUser,
    });
    return response;
  };

  /**
   * Creates a new work group for a user.
   * This function sends a POST request to the specified endpoint to create a work group
   * using the provided data.
   * @param {Object} data - The data required to create the work group.
   * @param {string} data.name - The name of the work group.
   * @param {string} data.description - A brief description of the work group.
   * @param {Array<string>} data.members - An array of user IDs to be added to the work group.
   * @returns {Promise<Object>} The response from the API containing the created work group info.
   * @throws {Error} Throws an error if the API request fails.
   */
  const create_work_group = async (data) => {
    const response = await axios.post(`${URL_USERS}/users/work-groups/`, data, {
      headers,
      endpointError: endpointError.user.create_work_group,
    });
    return response;
  };

  /**
   * Fetches the consultation working groups from the API.
   * @param {number} page - The page number for pagination.
   * @param {number} per_page - The number of results per page.
   * @param {string} [search] - Optional search term to filter the results by group name.
   * @returns {Promise<Object>} A promise that resolves to the response containing the working groups.
   * @throws {Error} Throws an error if the API call fails.
   */
  const ConsultationWorkingGroups = async (page, per_page, search) => {
    let response;
    if (isNullOrUndefined(search) === false) {
      response = await axios.get(
        `${URL_USERS}/users/work-groups/?page=${page}&per_page=${per_page}&name=${search}`,
        {
          headers,
          endpointError: endpointError.user.ConsultationWorkingGroups,
        }
      );
    } else {
      response = await axios.get(
        `${URL_USERS}/users/work-groups/?page=${page}&per_page=${per_page}`,
        {
          headers,
          endpointError: endpointError.user.ConsultationWorkingGroups,
        }
      );
    }
    return response;
  };

  /**
   * Gets the work group information for a user based on their role.
   * @param {string} id - The ID of the user whose work group is being retrieved.
   * @returns {Promise<Object>} - A promise that resolves to the response object containing user's work group data.
   * @throws {Error} If there is an issue fetching the work group data.
   */
  const getUserWorkGroupByRol = async (id) => {
    const response = await axios.get(`${URL_USERS}/users/work-groups/${id}/users/`, {
      headers,
      endpointError: endpointError.user.getUserWorkGroupByRol,
    });
    return response;
  };

  /**
   * Adds users to a specified work group.
   * This function sends a POST request to add members to a specific
   * work group identified by its ID. The request payload is expected
   * to be in a specific format, depending on the API requirements.
   * @param {string} id - The ID of the work group to which users will be added.
   * @param {Object} data - An object containing user data to be added to the work group.
   * @param {string} data.username - The username of the user to add.
   * @param {string} data.email - The email address of the user to add.
   * @returns {Promise<Object>} A promise that resolves to the response object from the API call.
   * @throws {Error} Throws an error if the API request fails or if the response is not successful.
   */
  const addUsersToWorkGroup = async (id, data) => {
    const response = await axios.post(`${URL_USERS}/users/work-groups/${id}/members/`, data, {
      headers,
      endpointError: endpointError.user.addUsersToWorkGroup,
    });
    return response;
  };

  /**
   * Fetches all work groups for users.
   * This function makes an asynchronous GET request to the specified
   * users' work groups endpoint, retrieving a maximum of 500 entries
   * from the first page of results. It is typically used to gather
   * and display work groups relevant to the user.
   * @returns {Promise<Object>} A promise that resolves to an object  containing the list of work groups.
   * @throws {AxiosError} If the request fails, an error object from  Axios will be thrown.
   */
  const consultAllGroupsWork = async () => {
    const responseAllGroups = await axios.get(
      `${URL_USERS}/users/work-groups/?page=${1}&per_page=${500}`,
      {
        headers,
        endpointError: endpointError.user.consultAllGroupsWork,
      }
    );
    return responseAllGroups.data;
  };

  /**
   * Updates a work group for a user with the specified ID.
   * This function sends a PATCH request to the server to update the work group
   * information for a user identified by the given ID.
   * @param {string} id - The unique identifier of the user whose work group is to be updated.
   * @param {Object} data - An object containing the work group data to update.
   * @param {string} data.name - The name of the work group.
   * @param {string} [data.description] - An optional description of the work group.
   * @returns {Promise<Object>} A promise that resolves to the response from the server.
   * @throws {Error} Throws an error if the request fails.
   */
  const updateWorkGroup = async (id, data) => {
    const response = await axios.patch(`${URL_USERS}/users/work-groups/${id}/`, data, {
      headers,
      endpointError: endpointError.user.updateWorkGroup,
    });
    return response;
  };

  /**
   * Fetches members of a specified work group.
   * @param {string} id - The identifier of the work group whose members are to be retrieved.
   * @param {number} page - The page number for pagination of results.
   * @param {number} per_page - The number of results to return per page.
   * @param {string} [search] - Optional. A search term to filter the members by name.
   * @returns {Promise<Object>} - A promise that resolves to the response object containing group members.
   * @throws {Error} - Throws an error if the request fails or if the response is not 2xx.
   * @example
   */
  const getMembersOfWorkGroup = async (id, page, per_page, search) => {
    let groupMembers;
    if (isNullOrUndefined(search) === false) {
      groupMembers = await axios.get(
        `${URL_USERS}/users/work-groups/${id}/members/?page=${page}&per_page=${per_page}&name=${search}`,
        {
          headers,
          endpointError: endpointError.user.getMembersOfWorkGroup,
        }
      );
    } else {
      groupMembers = await axios.get(
        `${URL_USERS}/users/work-groups/${id}/members/?page=${page}&per_page=${per_page}`,
        {
          headers,
          endpointError: endpointError.user.getMembersOfWorkGroup,
        }
      );
    }
    return groupMembers;
  };

  /**
   *Fetches a work group by its ID.
   * @param {string} id - The ID of the work group to retrieve.
   * @returns {Promise<Object>} The work group data.
   * @throws {Error} If the request fails.
   */
  const getWorkGroupById = async (id) => {
    const response = await axios.get(`${URL_USERS}/users/work-groups/${id}`, {
      headers,
      endpointError: endpointError.user.getWorkGroupById,
    });
    return response;
  };

  /**
   * Fetches work groups by their status.
   * @param {number} per_page - The number of items to return per page.
   * @param {string} status - The status of the work groups to retrieve.
   * @returns {Promise<Object>} A promise that resolves to the response object from the API call.
   * @throws {Error} Throws an error if the API call fails or if the response is not successful.
   */
  const getWorkGroupsByStatus = async (per_page, status) => {
    const response = await axios.get(
      `${URL_USERS}/users/work-groups/?page=1&per_page=${per_page}&status=${status}`,
      {
        headers,
        endpointError: endpointError.user.getWorkGroupsByStatus,
      }
    );
    return response;
  };

  /**
   * Deletes a member from a specified workgroup.
   * @param {string} workgroupUuid - The UUID of the workgroup from which the member is to be deleted.
   * @param {string} userId - The ID of the user to be removed from the workgroup.
   * @returns {Promise<Object>} The response from the axios delete request, which contains the server's response status and any relevant data.
   * @throws {Error} If the delete request fails, an error will be thrown with details.
   */
  const deleteMemberOfWorkGroup = async (workgroupUuid, userId) => {
    const responseDeleteMember = await axios.delete(
      `${URL_USERS}/users/work-groups/${workgroupUuid}/members/${userId}/`,
      {
        headers,
        endpointError: endpointError.user.deleteMemberOfWorkGroup,
      }
    );
    return responseDeleteMember;
  };

  /**
   * Generates a new API password for a Api user.
   * Sends a PATCH request to update the password for the specified user UUID.
   * @param {string} apiUserUuid - The UUID of the user for whom the API password is to be generated.
   * @returns {Promise<Object>} The response data from the API call.
   */
  const generateApiPassword = async (apiUserUuid, data) => {
    const response = await axios.patch(
      `${URL_USERS}/admin-api/users/${apiUserUuid}/password/`,
      data,
      {
        headers,
        endpointError: endpointError.user.generateApiPassword,
      }
    );
    return response;
  };

  return {
    getUsers,
    getApiUsers,
    createUser,
    createApiUser,
    updateUser,
    getUsersById,
    createPassword,
    updateStatus,
    emailPasswordReset,
    forgottenPasswordReset,
    getUserLogsById,
    changePasswordByUser,
    create_work_group,
    ConsultationWorkingGroups,
    getUserWorkGroupByRol,
    addUsersToWorkGroup,
    consultAllGroupsWork,
    updateWorkGroup,
    getMembersOfWorkGroup,
    getWorkGroupById,
    getWorkGroupsByStatus,
    deleteMemberOfWorkGroup,
    getUsersByUUID,
    generateApiPassword,
  };
}
